import { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, File, Download, Trash2, Paperclip, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/utils/cn';
import { financeV2Service } from '../services/financeV2Service';
import type { TransactionAttachment } from '../types/financeV2.types';

import { getErrorMessage } from '../utils/error.utils'
import { parseDbTimestamp } from '../utils/datetime'
interface TransactionAttachmentsProps {
  transactionId: string;
  onClose: () => void;
}

export function TransactionAttachments({ transactionId, onClose }: TransactionAttachmentsProps) {
  const [attachments, setAttachments] = useState<TransactionAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadAttachments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await financeV2Service.attachments.list(transactionId);
      setAttachments(response.data);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar anexos'));
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleUpload(Array.from(files));
    }
  };

  const handleUpload = async (files: File[]) => {
    try {
      setUploading(true);
      setError(null);
      setUploadProgress(`Enviando ${files.length} arquivo(s)...`);

      if (files.length === 1) {
        await financeV2Service.attachments.uploadSingle(transactionId, files[0]);
        setUploadProgress('Arquivo enviado com sucesso!');
      } else {
        const response = await financeV2Service.attachments.uploadMultiple(transactionId, files);
        const errors = response.data.errors;
        if (errors && errors.length > 0) {
          setError(`Alguns arquivos falharam: ${errors.join(', ')}`);
        }
        setUploadProgress(`${response.data.attachments.length} arquivo(s) enviado(s) com sucesso!`);
      }

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Reload attachments
      await loadAttachments();

      // Clear progress message after 3 seconds
      setTimeout(() => setUploadProgress(null), 3000);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao enviar arquivo'));
      setUploadProgress(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (attachment: TransactionAttachment) => {
    try {
      const blob = await financeV2Service.attachments.download(attachment.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao baixar arquivo'));
    }
  };

  const handleDelete = async (attachmentId: string) => {
    if (!confirm('Tem certeza que deseja excluir este anexo?')) {
      return;
    }

    try {
      setError(null);
      await financeV2Service.attachments.delete(attachmentId);
      await loadAttachments();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao excluir anexo'));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (date: string): string => {
    const parsed = parseDbTimestamp(date)
    if (!parsed) return '-'
    return parsed.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="lg" fullScreenOnMobile className="flex flex-col overflow-hidden sm:max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="size-5 text-muted-foreground" aria-hidden="true" />
            Anexos da Transação
          </DialogTitle>
          <DialogDescription>Documentos relacionados a este lançamento.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            id="file-upload"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.csv,.zip,.xml"
          />
          <label
            htmlFor="file-upload"
            className={cn(
              'flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed p-6 text-center transition-colors',
              uploading ? 'cursor-not-allowed border-border bg-muted' : 'cursor-pointer border-border hover:border-primary hover:bg-accent'
            )}
          >
            <Upload className={cn('mb-1 size-8', uploading ? 'text-muted-foreground' : 'text-primary')} aria-hidden="true" />
            <p className="text-sm font-medium">
              {uploading ? 'Enviando...' : 'Clique para selecionar arquivos'}
            </p>
            <p className="text-xs text-muted-foreground">
              PDF, Word, Excel, Imagens, ZIP (até 10MB por arquivo)
            </p>
          </label>

          {uploadProgress && (
            <div role="status" className="rounded-lg border bg-muted/40 p-3 text-sm">
              {uploadProgress}
            </div>
          )}

          {error && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
              <p className="min-w-0 break-words">{error}</p>
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col gap-2" aria-busy="true">
              <span className="sr-only">Carregando anexos...</span>
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-14" />)}
            </div>
          ) : attachments.length === 0 ? (
            <div className="flex flex-col items-center gap-1 py-8 text-center">
              <File className="mb-2 size-10 text-muted-foreground" aria-hidden="true" />
              <p className="text-sm font-medium">Nenhum anexo encontrado</p>
              <p className="text-sm text-muted-foreground">Faça upload de documentos relacionados a esta transação</p>
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {attachments.map((attachment) => (
                <li
                  key={attachment.id}
                  className="flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/50"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <File className="size-6 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{attachment.fileName}</p>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                        <span>{formatFileSize(attachment.fileSize)}</span>
                        <span aria-hidden="true">•</span>
                        <span>{formatDate(attachment.uploadedAt)}</span>
                        <span aria-hidden="true">•</span>
                        <span className="truncate">{attachment.uploaderName}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon-sm" onClick={() => handleDownload(attachment)} aria-label="Baixar">
                          <Download className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Baixar</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handleDelete(attachment.id)}
                          aria-label="Excluir"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Excluir</TooltipContent>
                    </Tooltip>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className="flex-row items-center justify-between sm:justify-between">
          <p className="text-sm text-muted-foreground">{attachments.length} anexo(s)</p>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
